import { accessRole, accessRoleLabel, TAccessRole } from 'src/app/infrastructure/models/access-role';

// Gast/vast van een DomainUser (plan gast/vast); de waarde is ook de zoektekst in de ledenlijst.
export const membershipKind = {
    permanent: 'permanent',
    guest: 'guest',
} as const;
export type TMembershipKind = (typeof membershipKind)[keyof typeof membershipKind];
import { isEmptyObject } from 'src/app/infrastructure/functions/is-empty-object.function';
import { domainUserDisplayName, firstFilled, userDisplayName, organisationLabel } from 'src/app/infrastructure/functions/user-name.function';
import { ILevelDomainListData } from 'src/app/infrastructure/models/level-domain-list-data';
import { domainDto } from 'src/assets/common/models/domain-dto.g';
import { IDomainUserListDto, domainUserListDto } from 'src/assets/common/models/domain-user-list-dto.g';
import { IInvitationDto, invitationDto } from 'src/assets/common/models/invitation-dto.g';
import { imageBoxDto } from 'src/assets/common/models/image-box-dto.g';
import { imageDto } from 'src/assets/common/models/image-dto.g';
import { logosDto } from 'src/assets/common/models/logos-dto.g';
import { userDto } from 'src/assets/common/models/user-dto.g';
import { dayToLocalDate, momentPassed } from 'src/app/infrastructure/functions/zone-day.function';

export const domainMemberKind = {
    domainUser: 'domainUser',
    invitation: 'invitation',
} as const;

export type TDomainMemberKind = (typeof domainMemberKind)[keyof typeof domainMemberKind];

export const domainUserListData = {
    requestedDomainUser_guid: 'requestedDomainUser_guid',
    domain_guid: 'domain_guid',

    guid: 'guid',
    nr: 'nr',
    // Optionele naam binnen het domain (vaak leeg). Voor weergave/zoeken/sorteren displayName gebruiken.
    name: 'name',
    // name → user_name (userDisplayName: applicationName → firstName + lastName → email). Zelfde sleutel als
    // domainUserData.displayName: searchFields lezen de string-waarde, niet het model.
    displayName: 'displayName',
    isAdmin: 'isAdmin',
    isOwner: 'isOwner',
    lastVisit: 'lastVisit',
    startDate: 'startDate',
    endDate: 'endDate',
    inactive: 'inactive',
    // Specifiek "toegangsperiode van dit lidmaatschap verlopen" (backend `inactive` omvat ook een
    // verwijderd/inactief domain of user). Afgeleid van het exclusieve eindmoment uit de dto: EndDate <= nu,
    // tijdzonevrij en gelijk aan de backend (zie ZoneClock).
    membershipInactive: 'membershipInactive',
    // Domain vereist 2FA (na de overgangsweek) en de gebruiker heeft het niet (backend-veld, alleen
    // gevuld in de eigen domainlijst op Home): de rij wordt gedimd, openen geeft TwoFactorRequired.
    twoFactorRequired: 'twoFactorRequired',
    deleted: 'deleted',
    // Afgeleid van inactive + membershipInactive hierboven — één veld voor de dimmed-marker in de
    // templates én (straks) de sorteer-/filterconfig, i.p.v. de OR-conditie op meerdere plekken te
    // herhalen. Zelfde sleutel als domainUserData.active (domain-user-data.ts): searchFields in
    // DomainComponent leest de string-waarde, niet het model, dus beide moeten identiek zijn.
    active: 'active',
    // Afgeleid van isOwner/isAdmin — numerieke hiërarchie (zie access-role.ts) i.p.v. tekst, zodat de
    // generieke sorteer-/filterpipe er correct op hiërarchie (owner→administrator→gebruiker) op kan
    // sorteren i.p.v. alfabetisch. Zelfde sleutel-conventie als active hierboven: domainUserData.
    // accessRole (domain-user-data.ts) moet dezelfde waarde hebben, want DomainComponent's
    // searchFields/sortFields lezen alleen de waarde, niet het model.
    accessRole: 'accessRole',
    // Gebruikersvriendelijke tegenhanger van accessRole hierboven — dezelfde tekst als de tooltip van
    // het rol-icoon in de template, zodat icoon en regel-2-tekst nooit uit elkaar kunnen lopen. Bewust
    // niet in searchFields/sortFields (zie DomainComponent): het numerieke accessRole hierboven is al
    // de sorteerbare/doorzoekbare representatie van dezelfde eigenschap.
    accessRoleLabel: 'accessRoleLabel',
    // Gast/vast (DomainUser.Permanent) als zoekbare tekst: filter '=guest' of '=permanent'. guestEndAuto = einddatum
    // komt van de gastregel (geen lopend project).
    membership: 'membership',
    guestEndAuto: 'guestEndAuto',
    // Organisatie als tweede kenmerk naast de naam: 'intern' of de contactnaam (organisationLabel); null = onbekend.
    // Zoekbaar.
    organisation_label: 'organisation_label',

    profilePhoto_guid: 'profilePhoto_thumbGuid',
    // Cache-bust (?v=) voor profilePhoto_guid: /api/Image/{guid} wordt immutable gecachet en de guid
    // blijft bij een bewerking gelijk — zonder versie bleef na F5/ander device de oude foto staan.
    profilePhoto_version: 'profilePhoto_version',
    domainPhoto_guid: 'domainPhoto_thumbGuid',
    userPhoto_guid: 'userPhoto_thumbGuid',

    domain_name: 'domain_name',
    domain_owner_email: 'domain_owner_email',
    domain_logoThumb_guid: 'domain_logoThumb_guid',

    user_name: 'user_name',
    user_email: 'user_email',

    domainDto: 'domainDto',
    ownerDto: 'ownerDto',

    // Alleen gevuld voor kind === invitation — gemapt vanuit IInvitationDto (zie createFromInvitation).
    kind: 'kind',
    invitation_status: 'invitation_status',
    invitation_isExpired: 'invitation_isExpired',
    invitation_invitedOn: 'invitation_invitedOn',
    // Domain heeft een 2FA-beleid en de geaccepteerde genodigde heeft nog geen 2FA (backend-veld).
    invitation_twoFactorMissing: 'invitation_twoFactorMissing',
} as const;

export type TDomainUserListData = (typeof domainUserListData)[keyof typeof domainUserListData];

export interface IDomainUserListData extends ILevelDomainListData {
    [domainUserListData.requestedDomainUser_guid]: string;
    [domainUserListData.domain_guid]: string;

    [domainUserListData.guid]: string;
    [domainUserListData.nr]: number | null;
    [domainUserListData.name]: string | null;
    [domainUserListData.displayName]: string;
    [domainUserListData.isAdmin]: boolean | null;
    [domainUserListData.isOwner]: boolean | null;
    [domainUserListData.lastVisit]: Date | null;
    [domainUserListData.startDate]: Date | null;
    [domainUserListData.endDate]: Date | null;
    [domainUserListData.deleted]: boolean;
    [domainUserListData.inactive]: boolean;
    [domainUserListData.membershipInactive]: boolean;
    [domainUserListData.twoFactorRequired]: boolean;
    [domainUserListData.active]: boolean;
    [domainUserListData.accessRole]: TAccessRole;
    [domainUserListData.accessRoleLabel]: string;
    [domainUserListData.membership]: TMembershipKind;
    [domainUserListData.guestEndAuto]: boolean;
    [domainUserListData.organisation_label]: string | null;

    [domainUserListData.profilePhoto_guid]: string | null;
    [domainUserListData.profilePhoto_version]: string | null;
    [domainUserListData.domainPhoto_guid]: string | null;
    [domainUserListData.userPhoto_guid]: string | null;

    [domainUserListData.domain_name]: string | null;
    [domainUserListData.domain_logoThumb_guid]: string | null;
    [domainUserListData.domain_owner_email]: string | null;

    [domainUserListData.user_name]: string | null;
    [domainUserListData.user_email]: string | null;

    [domainUserListData.kind]: TDomainMemberKind;
    [domainUserListData.invitation_status]: string | null;
    [domainUserListData.invitation_isExpired]: boolean | null;
    [domainUserListData.invitation_invitedOn]: Date | null;
    [domainUserListData.invitation_twoFactorMissing]: boolean;
}

export class DomainUserListData
    implements IDomainUserListData, ILevelDomainListData
{
    [domainUserListData.requestedDomainUser_guid]!: string;
    [domainUserListData.domain_guid]!: string;

    [domainUserListData.guid]!: string;
    [domainUserListData.nr]: number | null = null;
    [domainUserListData.name]: string | null = null;
    [domainUserListData.displayName]: string = '';
    [domainUserListData.isAdmin]: boolean | null = null;
    [domainUserListData.isOwner]: boolean | null = null;
    [domainUserListData.lastVisit]: Date | null = null;
    [domainUserListData.startDate]: Date | null = null;
    [domainUserListData.endDate]: Date | null = null;
    [domainUserListData.deleted]!: boolean;
    [domainUserListData.inactive]!: boolean;
    [domainUserListData.membershipInactive]: boolean = false;
    [domainUserListData.twoFactorRequired]: boolean = false;
    [domainUserListData.active]: boolean = true;
    [domainUserListData.accessRole]: TAccessRole = accessRole.user;
    [domainUserListData.accessRoleLabel]: string = accessRoleLabel[accessRole.user];
    [domainUserListData.membership]: TMembershipKind = membershipKind.permanent;
    [domainUserListData.guestEndAuto]: boolean = false;
    [domainUserListData.organisation_label]: string | null = null;

    [domainUserListData.profilePhoto_guid]: string | null = null;
    [domainUserListData.profilePhoto_version]: string | null = null;
    [domainUserListData.domainPhoto_guid]: string | null = null;
    [domainUserListData.userPhoto_guid]: string | null = null;
    
    [domainUserListData.domain_name]: string | null = null;
    [domainUserListData.domain_logoThumb_guid]: string | null = null;
    [domainUserListData.domain_owner_email]: string | null = null;

    [domainUserListData.user_name]: string | null = null;
    [domainUserListData.user_email]: string | null = null;

    [domainUserListData.kind]: TDomainMemberKind = domainMemberKind.domainUser;
    [domainUserListData.invitation_status]: string | null = null;
    [domainUserListData.invitation_isExpired]: boolean | null = null;
    [domainUserListData.invitation_invitedOn]: Date | null = null;
    [domainUserListData.invitation_twoFactorMissing]: boolean = false;

    private constructor(
        modelListDto?: IDomainUserListDto | null
    ) {
        if (!modelListDto || isEmptyObject(modelListDto)) return;
        const __lastVisit = modelListDto[domainUserListDto.lastVisit];
        const __endDate = modelListDto[domainUserListDto.endDate];

        this[domainUserListData.requestedDomainUser_guid] = modelListDto[domainUserListDto.requestedDomainUser_Guid]!;
        this[domainUserListData.domain_guid] = modelListDto[domainUserListDto.domain_Guid]!;

        this[domainUserListData.guid] = modelListDto[domainUserListDto.guid]!;
        this[domainUserListData.nr] = Number(modelListDto[domainUserListDto.nr]);
        this[domainUserListData.name] = modelListDto[domainUserListDto.name];
        this[domainUserListData.isAdmin] = Boolean(modelListDto[domainUserListDto.isAdmin]);
        this[domainUserListData.isOwner] = Boolean(modelListDto[domainUserListDto.isOwner]);
        // Zelfde precedentie als de rol-icoon-branches in domain-user-tablist.component.html
        // (owner > administrator > gebruiker) — één berekening i.p.v. de if/else-keten te herhalen
        // op elke plek die de rol nodig heeft. accessRoleLabel is de weergavetekst die daarbij hoort.
        this[domainUserListData.accessRole] = this[domainUserListData.isOwner] ? accessRole.owner
            : this[domainUserListData.isAdmin] ? accessRole.administrator
            : accessRole.user;
        this[domainUserListData.accessRoleLabel] = accessRoleLabel[this[domainUserListData.accessRole]];
        this[domainUserListData.membership] = (modelListDto[domainUserListDto.permanent] ?? true) ? membershipKind.permanent : membershipKind.guest;
        this[domainUserListData.guestEndAuto] = Boolean(modelListDto[domainUserListDto.guestEndAuto]);
        this[domainUserListData.organisation_label] = organisationLabel(modelListDto[domainUserListDto.affiliation], modelListDto[domainUserListDto.organisation_Name]);
        this[domainUserListData.lastVisit] = !!__lastVisit ? new Date(__lastVisit) : null;
        // Kalenderdagen in de zone van het domain (StartDay/EndDay; endDate = laatste dag mét toegang).
        this[domainUserListData.startDate] = dayToLocalDate(modelListDto[domainUserListDto.startDay]);
        this[domainUserListData.endDate] = dayToLocalDate(modelListDto[domainUserListDto.endDay]);
        this[domainUserListData.membershipInactive] = momentPassed(__endDate);
        this[domainUserListData.domain_guid] = modelListDto[domainUserListDto.domain_Guid]!;
        this[domainUserListData.deleted] = Boolean(modelListDto[domainUserListDto.deleted]);
        this[domainUserListData.inactive] = Boolean(modelListDto[domainUserListDto.inactive]);
        this[domainUserListData.twoFactorRequired] = Boolean(modelListDto[domainUserListDto.twoFactorRequired]);
        this[domainUserListData.active] = !this[domainUserListData.membershipInactive] && !this[domainUserListData.inactive]
            && !this[domainUserListData.twoFactorRequired];

        // Fallback: geen domain-specifieke profielfoto gekozen (DomainUser.ProfileFileBoxId) →
        // toon de Home-hoofdfoto van de User (User.PhotoFileBoxId) i.p.v. niets — zelfde patroon
        // als domain-user-data.ts (detailpagina).
        const __profileBox = modelListDto[domainUserListDto.profilePhotoBoxDto] ?? modelListDto[domainUserListDto.userPhotoBoxDto];
        this[domainUserListData.profilePhoto_guid] = __profileBox?.[imageBoxDto.photoThumbDto]?.[imageDto.guid] ?? null;
        // Zelfde versie-bron als image-list-data.ts: thumb, anders het origineel.
        this[domainUserListData.profilePhoto_version] = __profileBox?.[imageBoxDto.photoThumbDto]?.[imageDto.modifiedOn]
            ?? __profileBox?.[imageBoxDto.photoDto]?.[imageDto.modifiedOn]
            ?? null;
        this[domainUserListData.domainPhoto_guid] = modelListDto[domainUserListDto.domainDto]?.[domainDto.photoBoxDto]?.[imageBoxDto.photoThumbDto]?.[imageDto.guid] ?? null;
        this[domainUserListData.userPhoto_guid]  = modelListDto[domainUserListDto.userDto]?.[userDto.photoBoxDto]?.[imageBoxDto.guid] ?? null;

        this[domainUserListData.domain_name] = modelListDto[domainUserListDto.domainDto]?.[domainDto.name] ?? null;
        this[domainUserListData.domain_logoThumb_guid] = modelListDto[domainUserListDto.domainDto]?.[domainDto.logosDto]?.[logosDto.logoThumbDto]?.[imageDto.guid] ?? null;
        this[domainUserListData.domain_owner_email] = modelListDto[domainUserListDto.ownerDto]?.[userDto.email] ?? null;

        // Getoonde naam van de User (applicationName → firstName + lastName → email).
        this[domainUserListData.user_name] = userDisplayName(modelListDto[domainUserListDto.userDto]) || null;
        this[domainUserListData.user_email] = modelListDto[domainUserListDto.userDto]?.[userDto.email] ?? null;
        this[domainUserListData.displayName] = domainUserDisplayName(this[domainUserListData.name], this[domainUserListData.user_name]);
    }

    static create(instance: boolean): IDomainUserListData | null;
    static create(modelListDto?: IDomainUserListDto | null): IDomainUserListData | null;
    static create(value1?: boolean | IDomainUserListDto | null): IDomainUserListData | null {

        if (typeof(value1) == 'boolean') {
            return new DomainUserListData();
        } else {
            const _modelListDto = value1;

            return _modelListDto ? new DomainUserListData(_modelListDto) : null;
        }
    }

    // Vertaalt een pending Invitation naar dezelfde rij-vorm als een DomainUser, zodat beide
    // in één lijst getoond kunnen worden (kind onderscheidt de weergave in de rij-template).
    static createFromInvitation(invitation: IInvitationDto): IDomainUserListData {
        const __instance = new DomainUserListData();

        __instance[domainUserListData.guid] = invitation[invitationDto.guid] ?? '';
        __instance[domainUserListData.requestedDomainUser_guid] = '';
        __instance[domainUserListData.domain_guid] = '';
        __instance[domainUserListData.deleted] = false;
        __instance[domainUserListData.inactive] = false;

        const __createdOn = invitation[invitationDto.createdOn];

        __instance[domainUserListData.user_email] = invitation[invitationDto.email];
        __instance[domainUserListData.displayName] = firstFilled(invitation[invitationDto.email]);
        __instance[domainUserListData.kind] = domainMemberKind.invitation;
        __instance[domainUserListData.invitation_status] = invitation[invitationDto.status];
        __instance[domainUserListData.invitation_isExpired] = invitation[invitationDto.isExpired];
        __instance[domainUserListData.invitation_invitedOn] = __createdOn ? new Date(__createdOn) : null;
        __instance[domainUserListData.invitation_twoFactorMissing] = Boolean(invitation[invitationDto.acceptedUser_TwoFactorMissing]);

        return __instance;
    }
}
